import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests. Also hard-block writes for the read-only HQ Viewer
// role (mirrors the server-side guard) so accidental edits never even leave the
// browser — the backend still rejects them regardless.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const method = (config.method || 'get').toLowerCase();
  if (!['get', 'head', 'options'].includes(method)) {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const positions = u.positions || [];
      const readOnly = positions.includes('HQ Viewer') && !positions.includes('Admin');
      const url = config.url || '';
      const alwaysAllowed = url.includes('/auth/change-password') || url.includes('/privacy-policy/accept');
      if (readOnly && !alwaysAllowed) {
        return Promise.reject({ __readonly: true, config });
      }
    } catch { /* ignore */ }
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error && error.__readonly) {
      toast.error('Read-only access — HQ Viewer cannot make changes.');
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (name, email, password) => api.post('/auth/register', { name, email, password });
export const getMe = () => api.get('/auth/me');
export const getSystemStatus = () => api.get('/auth/system-status');
export const requestPasswordReset = (email) => api.post('/auth/request-reset', { email });
export const resetPassword = (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword });
export const changePassword = (currentPassword, newPassword) => {
  const formData = new URLSearchParams();
  formData.append('current_password', currentPassword);
  formData.append('new_password', newPassword);
  return api.put('/auth/change-password', formData, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
  });
};

// User Settings
export const getUserSettings = () => api.get('/auth/settings');
export const updateUserSettings = (settings) => api.put('/auth/settings', settings);

// Platform Admin (super admin only) — multi-tenant SaaS management
export const platformListSchools = () => api.get('/platform/schools');
export const platformCreateSchool = (payload) => api.post('/platform/schools', payload);
export const platformSuggestPassword = () => api.get('/platform/suggest-password');
export const platformSetSchoolActive = (schoolId, active) => api.patch(`/platform/schools/${schoolId}/active`, { active });
export const platformGetPrivacyPolicy = () => api.get('/platform/privacy-policy');
export const platformPublishPrivacyPolicy = (content) => api.post('/platform/privacy-policy', { content });
export const getCurrentPrivacyPolicy = () => api.get('/privacy-policy/current');
export const acceptPrivacyPolicy = () => api.post('/privacy-policy/accept');
export const getAssessmentRubric = () => api.get('/assessment-rubric');
export const updateAssessmentRubric = (image) => api.put('/assessment-rubric', { image });

// Public school directory (student self-assessment portal picker)
export const getPublicSchools = () => api.get('/schools/public');

// Students
export const getStudents = (status = null, includeSummerInactive = false, asOfDate = null, graduationStatus = null) => {
  const params = {};
  if (status) params.status = status;
  if (includeSummerInactive) params.include_summer_inactive = true;
  if (asOfDate) params.as_of_date = asOfDate;
  if (graduationStatus) params.graduation_status = graduationStatus;
  return api.get('/students', { params });
};
export const getSummerStatus = () => api.get('/students/summer-status');
export const getStudent = (id) => api.get(`/students/${id}`);
export const createStudent = (data) => api.post('/students', data);
export const updateStudent = (id, data) => api.put(`/students/${id}`, data);
export const deleteStudent = (id) => api.delete(`/students/${id}`);
export const getFormExpiry = (withinDays = 30) => api.get('/forms/expiry', { params: { within_days: withinDays } });
export const sendFormExpiryReminder = () => api.post('/forms/expiry/send-reminder');
export const scanDuplicates = () => api.get('/duplicates/scan');
export const getProficiencyCatalog = () => api.get('/proficiency/catalog');
export const createProficiencyIndicator = (item) => api.post('/proficiency/catalog', item);
export const updateProficiencyIndicator = (id, updates) => api.put(`/proficiency/catalog/${id}`, updates);
export const deleteProficiencyIndicator = (id) => api.delete(`/proficiency/catalog/${id}`);
export const getProficiencyForStudent = (studentId, semester) =>
  api.get(`/proficiency/student/${studentId}`, { params: semester ? { semester } : {} });
export const upsertProficiencyRecord = (record) => api.post('/proficiency/record', record);
export const overrideAchieved = (payload) => api.post('/proficiency/override-achieved', payload);
export const getStudentThresholdOverrides = (studentId) =>
  api.get(`/proficiency/student/${studentId}/threshold-overrides`);
export const setStudentThresholdOverride = (studentId, payload) =>
  api.put(`/proficiency/student/${studentId}/threshold-override`, payload);
export const getProficiencyGrid = (params) => api.get('/proficiency/bulk-grid', { params });
export const bulkSaveProficiency = (payload) => api.post('/proficiency/bulk-save', payload);
export const getAssessmentCategoryOptions = () => api.get('/proficiency/assessment-categories');

// Branding
export const getBranding = () => api.get('/branding');
export const updateBranding = (updates) => api.put('/branding', updates);
export const uploadBrandingLogo = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/branding/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Progress narratives & PDF reports
export const listSubjectNarratives = (params) => api.get('/progress/subject-narratives', { params });
export const upsertSubjectNarrative = (payload) => api.put('/progress/subject-narrative', payload);
export const listStudentComments = (params) => api.get('/progress/student-comments', { params });
export const upsertStudentComment = (payload) => api.put('/progress/student-comment', payload);
export const progressReportPdfUrl = (studentId, semester, school_year) => {
  const params = new URLSearchParams({ student_id: studentId, semester, school_year });
  return `${process.env.REACT_APP_BACKEND_URL}/api/progress/report-pdf?${params}`;
};
export const progressReportBatchPdfUrl = (studentIds, semester, school_year) => {
  const params = new URLSearchParams({ student_ids: studentIds.join(','), semester, school_year });
  return `${process.env.REACT_APP_BACKEND_URL}/api/progress/report-pdf-batch?${params}`;
};
export const saveProgressReportsToFiles = (payload) => api.post('/progress/save-to-files', payload);
export const getProgressArchiveStatus = (semester, school_year) =>
  api.get('/progress/archive-status', { params: { semester, school_year } });
// Progress Tracking roster for a period (excludes referral/in-progress/waitlist;
// graduated students only appear for periods within their enrollment).
export const getProgressEligibleStudents = (semester, school_year) =>
  api.get('/progress/eligible-students', { params: { semester, school_year } });

// School settings — grading system (proficiency | traditional), locked after setup.
export const getSchoolSettings = () => api.get('/school-settings');
export const setGradingSystem = (grading_system) => api.post('/school-settings/grading', { grading_system });
export const updateGradingSubjects = (subjects) => api.put('/school-settings/grading-subjects', { subjects });

// Traditional (letter-grade) grades
export const getTraditionalGrades = (semester, school_year) =>
  api.get('/traditional-grades', { params: { semester, school_year } });
export const saveTraditionalGrades = (payload) => api.post('/traditional-grades/bulk-save', payload);


// Setup wizard
export const getSetupProgress = () => api.get('/setup/progress');
export const updateSetupProgress = (payload) => api.put('/setup/progress', payload);
export const getDemoStatus = () => api.get('/setup/demo-status');
export const demoReset = (payload) => api.post('/setup/demo-reset', payload);
export const removeSampleData = () => api.post('/setup/remove-sample-data');
export const downloadProficiencyTemplate = (semester, school_year, indicator_type = 'all') => {
  const params = new URLSearchParams({ semester, school_year, indicator_type });
  return `${process.env.REACT_APP_BACKEND_URL}/api/proficiency/import-template?${params}`;
};
export const importProficiencyRecords = (file, semester, school_year) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('semester', semester);
  fd.append('school_year', school_year);
  return api.post('/proficiency/import-records', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const updateStudentSchedule = (id, schedule) => api.put(`/students/${id}/schedule`, { schedule });
export const flagScheduleReview = (id, note, reviewDate) => api.post(`/students/${id}/schedule-review`, { note, review_date: reviewDate || null });
export const clearScheduleReview = (id, nextReviewDate) => api.delete(`/students/${id}/schedule-review`, { params: nextReviewDate ? { next_review_date: nextReviewDate } : {} });
export const downloadImportTemplate = () => api.get('/students/import/template', { responseType: 'blob' });
export const exportStudents = () => api.get('/students/export', { responseType: 'blob' });
export const assignStudentNumbers = () => api.post('/students/assign-numbers');

export const fixStudentStatus = () => api.post('/students/fix-status');
export const bulkImportStudents = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  
  const response = await fetch(`${baseUrl}/api/students/import`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Import failed' }));
    throw { response: { data: error } };
  }
  
  return { data: await response.json() };
};

// Permissions
export const getPermissions = () => api.get('/permissions');
export const updateRolePermissions = (role, permissions) => api.put(`/permissions/${role}`, { role, permissions });
export const resetPermissions = () => api.post('/permissions/reset');

// Attendance
export const getAttendance = (params) => api.get('/attendance', { params });
export const getInBuilding = (date) => api.get('/attendance/in-building', { params: date ? { date } : {} });
export const checkInStudents = (records) => api.post('/attendance/check-in', records);
export const checkOutStudents = (records) => api.post('/attendance/check-out', records);
export const updateAttendanceStatus = (studentId, date, status, checkInTime = null) => 
  api.put(`/attendance/${studentId}/status`, { date, status, ...(checkInTime && { check_in_time: checkInTime }) });
export const recordAbsence = (data) => api.post('/attendance/absence', data, {
  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
});
export const getAbsences = (date) => api.get(`/attendance/absences/${date}`);

// Weekly Sheet (Weekly Transportation & Attendance Overview)
export const getWeeklySheet = (weekStart) => api.get('/weekly-sheet', { params: weekStart ? { week_start: weekStart } : {} });
export const saveWeeklySheetCell = (studentId, date, value) => api.put('/weekly-sheet/cell', { student_id: studentId, date, value });

// Assessments
export const getAssessments = (params) => api.get('/assessments', { params });
export const createAssessment = (data) => api.post('/assessments', data);
export const createBatchAssessments = (data) => api.post('/assessments/batch', data);
export const getDidNotAttendReport = (date) => api.get('/assessments/did-not-attend', { params: { date } });
export const updateAssessment = (id, data) => api.put(`/assessments/${id}`, data);
export const deleteAssessment = (id) => api.delete(`/assessments/${id}`);
export const getStudentAssessmentHistory = (studentId, params) => api.get(`/assessments/history/${studentId}`, { params });
export const autoAssignOnes = (date) => api.post('/assessments/auto-assign-ones', { date });
export const getDnaReview = (date) => api.get('/assessments/dna-review', { params: { date } });
export const saveDnaReview = (id, data) => api.post(`/assessments/${id}/dna-review`, data);
export const scanInvalidDna = () => api.get('/assessments/invalid-dna');
export const cleanupInvalidDna = () => api.post('/assessments/invalid-dna/cleanup');
export const scanAutoCheckout = () => api.get('/attendance/auto-checkout-review');
export const fixAutoCheckout = () => api.post('/attendance/auto-checkout-review/fix');
export const migrateAutoOnes = () => api.post('/assessments/migrate-auto-ones');
export const getAssessmentReport = (params) => api.get('/assessments/report', { params });
export const getAssessmentTrend = (params) => api.get('/assessments/trend', { params });

// Custom Groups
export const getCustomGroups = () => api.get('/custom-groups');
export const createCustomGroup = (data) => api.post('/custom-groups', data);
export const updateCustomGroup = (id, data) => api.put(`/custom-groups/${id}`, data);
export const deleteCustomGroup = (id) => api.delete(`/custom-groups/${id}`);

// Clinical Plans
// BRP (Behavioral Redirection Plans) — all-students table over clinical_plans
export const getBrp = () => api.get('/brp');
export const appendBrpLog = (studentId, text) => api.post(`/brp/${studentId}/log`, { text });
export const editBrpLogEntry = (studentId, entryId, data) => api.patch(`/brp/${studentId}/log/${entryId}`, data);
export const deleteBrpLogEntry = (studentId, entryId) => api.delete(`/brp/${studentId}/log/${entryId}`);
export const updateBrp = (studentId, data) => api.patch(`/brp/${studentId}`, data);
export const reviewAllBrp = () => api.post('/brp/review-all');

export const getClinicalPlans = (studentId) => api.get('/clinical-plans', { params: { student_id: studentId } });
export const getArchivedClinicalPlans = (studentId) => api.get('/clinical-plans', { params: { student_id: studentId, status: 'archived' } });
export const getClinicalPlansDueForReview = (daysAhead = 7) => api.get('/clinical-plans/due-for-review', { params: { days_ahead: daysAhead } });
export const getMyClinicalReviewCount = () => api.get('/clinical-plans/my-review-count');
export const clearAllClinicalReviews = () => api.post('/clinical-plans/clear-all-reviews');export const createClinicalPlan = (data) => api.post('/clinical-plans', data);
export const updateClinicalPlan = (id, data) => api.put(`/clinical-plans/${id}`, data);
export const deleteClinicalPlan = (id) => api.delete(`/clinical-plans/${id}`);
export const markClinicalPlanReviewed = (id) => api.post(`/clinical-plans/${id}/review`);
export const getClinicalPlanReviewStatus = (id) => api.get(`/clinical-plans/${id}/review-status`);
export const getOverdueClinicalReviews = () => api.get('/clinical-plans/overdue-reviews');

// Advisory
export const getAdvisorySummary = (advisoryGroup, weekStart) => 
  api.get('/advisory/summary', { params: { advisory_group: advisoryGroup, week_start: weekStart } });
export const getAdvisoryGroups = () => api.get('/advisory/groups');
export const assignStudentsToAdvisories = () => api.post('/advisory/assign-students');

// School Year Calendar
export const getSchoolCalendars = (academicYear) => 
  api.get('/school-calendar', { params: academicYear ? { academic_year: academicYear } : {} });
export const createSchoolCalendar = (data) => api.post('/school-calendar', data);
export const updateSpecialDates = (academicYear, specialDates) => 
  api.put(`/school-calendar/${academicYear}/special-dates`, specialDates);
export const deleteSchoolCalendar = (academicYear) => api.delete(`/school-calendar/${academicYear}`);

// Reports
export const createIncidentReport = (data) => api.post('/reports/incident', data);
export const updateIncidentReport = (reportId, data) => api.put(`/reports/incident/${reportId}`, data);
export const deleteIncidentReport = (reportId) => api.delete(`/reports/incident/${reportId}`);
export const getIncidentReports = (params) => api.get('/reports/incidents', { params });
// OTC / Medical logs
export const getMedicalLogs = (params = {}) => api.get('/medical-logs', { params });
export const createMedicalLog = (data) => api.post('/medical-logs', data);
export const updateMedicalLog = (id, data) => api.put(`/medical-logs/${id}`, data);
export const deleteMedicalLog = (id) => api.delete(`/medical-logs/${id}`);

export const getHardcopyAccess = (params = {}) => api.get('/hardcopy-access', { params });
export const createHardcopyAccess = (data) => api.post('/hardcopy-access', data);
export const createHardcopyAccessBatch = (data) => api.post('/hardcopy-access/batch', data);
export const getAttendanceSummary = (params) => api.get('/reports/attendance-summary', { params });
export const getAtRiskStudents = (params) => api.get('/reports/at-risk', { params });
export const getBirthdays = (params) => api.get('/reports/birthdays', { params });
export const getGroupLists = () => api.get('/reports/groups');
export const getSnapshotReport = (params = {}) => api.get('/reports/snapshot', { params });
export const emailSnapshotReport = (payload) => api.post('/reports/snapshot/email', payload);
export const getSnapshotDistrictContacts = () => api.get('/reports/snapshot/district-contacts');
export const emailSnapshotBulk = (payload) => api.post('/reports/snapshot/email-bulk', payload);
export const emailSnapshotBulkSelf = (payload) => api.post('/reports/snapshot/email-bulk-self', payload);
export const previewBilling = (startDate, endDate, district = null) => 
  api.get('/reports/billing-preview', { params: { start_date: startDate, end_date: endDate, district: district } });
export const exportBilling = (startDate, endDate, district = null) => 
  api.get('/reports/billing-export', { 
    params: { start_date: startDate, end_date: endDate, district: district },
    responseType: 'blob'
  });
export const getAttendanceByStudent = (startDate, endDate, filterType = 'all', filterValue = null) =>
  api.get('/reports/attendance-by-student', { 
    params: { 
      start_date: startDate, 
      end_date: endDate,
      filter_type: filterType,
      filter_value: filterValue
    }
  });
export const getStudentAttendanceTrend = (studentId) =>
  api.get('/reports/student-attendance-trend', { params: { student_id: studentId } });
export const exportAttendanceByStudent = (startDate, endDate, filterType = 'all', filterValue = null) =>
  api.get('/reports/attendance-by-student/export', { 
    params: { 
      start_date: startDate, 
      end_date: endDate,
      filter_type: filterType,
      filter_value: filterValue
    },
    responseType: 'blob'
  });
export const getDailyAttendanceSummary = (startDate, endDate) =>
  api.get('/reports/daily-attendance-summary', {
    params: { start_date: startDate, end_date: endDate }
  });
export const getStudentAttendanceHours = (startDate, endDate) =>
  api.get('/reports/student-attendance-hours', {
    params: { start_date: startDate, end_date: endDate }
  });

// Services
export const createService = (data) => api.post('/services', data);
export const getStudentServices = (studentId) => api.get(`/services/${studentId}`);
export const updateService = (serviceId, data) => api.put(`/services/${serviceId}`, data);
export const deleteService = (serviceId) => api.delete(`/services/${serviceId}`);
export const importServices = (formData) => api.post('/services/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Files
export const uploadFile = (studentId, fileType, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/upload/${studentId}/${fileType}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getStudentFiles = (studentId, fileType) => 
  api.get(`/files/${studentId}`, { params: { file_type: fileType } });
export const downloadFile = (fileId) => 
  api.get(`/files/download/${fileId}`, { responseType: 'blob' });

export const deleteFile = (fileId) => api.delete(`/files/${fileId}`);
export const updateFileCategory = (fileId, fileType) =>
  api.put(`/files/${fileId}/category`, { file_type: fileType });
export const getStudentsMissingDocuments = () => api.get('/students/missing-documents');
export const getDistrictAudit = () => api.get('/data/district-audit');
export const setDistrictAudit = (student_id, district) => api.post('/data/district-audit/set', { student_id, district });
export const setDistrictAuditBulk = (student_ids, district) => api.post('/data/district-audit/set-bulk', { student_ids, district });
// Calendar
export const getCalendarEvents = (params) => api.get('/calendar/events', { params });
export const createCalendarEvent = (data) => api.post('/calendar/events', data);
export const updateCalendarEvent = (id, data) => api.put(`/calendar/events/${id}`, data);
export const deleteCalendarEvent = (id) => api.delete(`/calendar/events/${id}`);
export const getRoster = (date) => api.get('/schedule/roster', { params: { date } });
export const getWeekSchoolDays = (startDate, endDate) => api.get('/schedule/school-days', { params: { start_date: startDate, end_date: endDate } });

// Staff
export const getStaff = (params) => api.get('/staff', { params });
export const getClinicians = () => api.get('/staff/clinicians');
export const getSpecialEducators = () => api.get('/staff/special-educators');
export const createStaff = (data) => api.post('/staff', data);
export const updateStaff = (id, data) => api.put(`/staff/${id}`, data);
export const resetStaffPassword = (id, newPassword) => api.put(`/staff/${id}/reset-password`, { new_password: newPassword });
export const deleteStaff = (id) => api.delete(`/staff/${id}`);
export const getLicenseAlerts = () => api.get('/staff/license-alerts');
export const checkLicenseExpirations = () => api.post('/staff/check-license-expirations');

// Districts
export const getDistricts = () => api.get('/districts');
export const createDistrict = (data) => api.post('/districts', data);
export const updateDistrict = (id, data) => api.put(`/districts/${id}`, data);
export const deleteDistrict = (id) => api.delete(`/districts/${id}`);
export const exportDistricts = () => api.get('/districts/export', { responseType: 'blob' });
export const importDistricts = (formData) => api.post('/districts/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const scanDistrictDuplicates = () => api.get('/districts/duplicates');
export const mergeDistricts = (data) => api.post('/districts/merge', data);
export const listDistrictAliases = () => api.get('/districts/aliases');
export const createDistrictAlias = (data) => api.post('/districts/aliases', data);
export const deleteDistrictAlias = (id) => api.delete(`/districts/aliases/${id}`);

// ===== District Portal (external LEA / Medicaid Billing logins) =====
// Admin management (School Admin only)
export const portalAdminGetDistricts = () => api.get('/portal-admin/districts');
export const portalAdminGetProviders = () => api.get('/portal-admin/providers');
// Staff-accessible list of transportation provider names (student-file dropdown)
export const getTransportationProviders = () => api.get('/transportation-providers');
export const getTransportProviderContacts = () => api.get('/transportation-provider-contacts');
export const setTransportProviderContact = (name, phone) => api.post('/portal-admin/providers/contact', { name, phone });
export const portalAdminListUsers = (arg) => api.get('/portal-admin/users', {
  params: typeof arg === 'string' ? (arg ? { district: arg } : {}) : (arg || {}),
});
export const portalAdminCreateUser = (payload) => api.post('/portal-admin/users', payload);
export const portalAdminUpdateUser = (id, payload) => api.put(`/portal-admin/users/${id}`, payload);
export const portalAdminResetPassword = (id) => api.post(`/portal-admin/users/${id}/reset-password`);
export const portalAdminDeleteUser = (id) => api.delete(`/portal-admin/users/${id}`);
// Transportation provider cleanup (admin)
export const renameTransportProvider = (old_name, new_name) => api.post('/portal-admin/providers/rename', { old_name, new_name });
export const mergeTransportProviders = (sources, target) => api.post('/portal-admin/providers/merge', { sources, target });

// Historical attendance import (admin)
export const getAttendanceImportYears = () => api.get('/attendance-import/school-years');
export const attendanceImportTemplateUrl = () => `${process.env.REACT_APP_BACKEND_URL}/api/attendance-import/template`;
export const attendanceImportPreview = (file, school_year) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('school_year', school_year);
  return api.post('/attendance-import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const attendanceImportCommit = (file, school_year) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('school_year', school_year);
  return api.post('/attendance-import/commit', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Historical assessment import
export const getAssessmentImportYears = () => api.get('/assessment-import/school-years');
export const assessmentImportPreview = (file, school_year) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('school_year', school_year);
  return api.post('/assessment-import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const assessmentImportCommit = (file, school_year) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('school_year', school_year);
  return api.post('/assessment-import/commit', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Historical incident report import (admin, Excel)
export const incidentImportPreview = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/incident-import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const incidentImportCommit = (payload) => api.post('/incident-import/commit', payload);

// Portal user (external rep) self-service
export const getPortalMe = () => api.get('/portal/me');
export const updatePortalProfile = (payload) => api.put('/portal/profile', payload);
export const portalChangePassword = (currentPassword, newPassword) => {
  const fd = new URLSearchParams();
  fd.append('current_password', currentPassword);
  fd.append('new_password', newPassword);
  return api.put('/portal/change-password', fd, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
};
export const getPortalStudents = () => api.get('/portal/students');
export const getPortalAttendanceSummary = (startDate, endDate) =>
  api.get('/portal/attendance-summary', { params: { start_date: startDate, end_date: endDate } });
export const getPortalAssessments = (startDate, endDate) =>
  api.get('/portal/assessments', { params: { start_date: startDate || undefined, end_date: endDate || undefined } });
export const getPortalProgressReport = (studentId, semester, schoolYear) =>
  api.get('/portal/progress-report', { params: { student_id: studentId, semester, school_year: schoolYear }, responseType: 'blob' });
export const getPortalContracts = () => api.get('/portal/contracts');
export const getPortalContractFile = (fileId) =>
  api.get(`/portal/contract/file/${fileId}`, { responseType: 'blob' });
export const getPortalServices = () => api.get('/portal/services');
export const exportPortalServices = () => api.get('/portal/services/export', { responseType: 'blob' });
export const createPortalService = (payload) => api.post('/portal/services', payload);
export const updatePortalService = (id, payload) => api.put(`/portal/services/${id}`, payload);
export const deletePortalService = (id) => api.delete(`/portal/services/${id}`);

// Transportation provider portal (self-service)
export const getPortalTransportStudents = () => api.get('/portal/transport-students');
export const getPortalTransportWeeklySheet = (weekStart) =>
  api.get('/portal/transport-weekly-sheet', { params: weekStart ? { week_start: weekStart } : {} });
export const updatePortalTransportTimes = (studentId, payload) =>
  api.put(`/portal/transport-students/${studentId}/times`, payload);
export const getPortalDistrictStudents = () => api.get('/portal/district-students');
export const updatePortalDistrictTimes = (studentId, payload) =>
  api.put(`/portal/district-students/${studentId}/times`, payload);

// ===== Parent / Guardian Portal =====
// Parent (self-service, read-only)
export const getParentMe = () => api.get('/parent/me');
export const getParentStudentDemographics = (studentId) => api.get(`/parent/student/${studentId}`);
export const getParentStudentAttendance = (studentId, startDate, endDate) =>
  api.get(`/parent/student/${studentId}/attendance`, { params: { start_date: startDate, end_date: endDate } });
export const getParentStudentAssessments = (studentId, startDate, endDate) =>
  api.get(`/parent/student/${studentId}/assessments`, { params: { start_date: startDate || undefined, end_date: endDate || undefined } });
export const updateParentContactInfo = (studentId, data) =>
  api.put(`/parent/student/${studentId}/contact-info`, data);

// Read-only school calendar shared by all portal roles (parent/district/transport)
export const getPortalSchoolCalendar = (academicYear) =>
  api.get('/portal/school-calendar', { params: academicYear ? { academic_year: academicYear } : {} });
export const getPortalCalendarEvents = (startDate, endDate) =>
  api.get('/portal/calendar-events', { params: { start_date: startDate, end_date: endDate } });

// Admin management of a student's guardian emails
export const listStudentGuardians = (studentId) => api.get(`/parent-admin/students/${studentId}/guardians`);
export const addStudentGuardian = (studentId, payload) => api.post(`/parent-admin/students/${studentId}/guardians`, payload);
export const removeStudentGuardian = (studentId, email) =>
  api.post(`/parent-admin/students/${studentId}/guardians/remove`, { email });
export const resetStudentGuardianPassword = (studentId, email) =>
  api.post(`/parent-admin/students/${studentId}/guardians/reset`, { email });
export const getParentSettings = (studentId) => api.get(`/parent-admin/students/${studentId}/settings`);
export const updateParentSettings = (studentId, enabled) =>
  api.put(`/parent-admin/students/${studentId}/settings`, { enabled });
export const updatePickupAlerts = (studentId, enabled) =>
  api.put(`/parent-admin/students/${studentId}/pickup-alerts`, { enabled });
export const notifyProgressReport = (studentId) =>
  api.post(`/parent-admin/students/${studentId}/notify-progress-report`);
export const getParentEditHistory = (studentId) =>
  api.get(`/parent-admin/students/${studentId}/edit-history`);
export const getEmailSuppressions = () => api.get(`/parent-admin/suppressions`);
export const removeEmailSuppression = (idOrEmail) =>
  api.post(`/parent-admin/suppressions/remove`, { id_or_email: idOrEmail });


// App Settings
export const getAppSettings = () => api.get('/app-settings');
export const updateAppSettings = (data) => api.put('/app-settings', null, { params: data });
export const exportAllData = () => api.get('/export/all-data', { responseType: 'blob' });

// Audit Log
export const getAuditLog = (limit = 100, actionType = null) => 
  api.get('/audit-log', { params: { limit, action_type: actionType } });

// Data Import / New Year
export const getPreviousYearStudents = () => api.get('/students/previous-year');
export const archiveCurrentStudents = () => api.post('/students/archive-current');
export const reenrollStudents = (studentIds, clearAttendance = true, clearAssessments = true) => 
  api.post('/students/reenroll', studentIds, { params: { clear_attendance: clearAttendance, clear_assessments: clearAssessments } });
export const startNewYear = (archiveStudents = true, clearAttendance = true, clearAssessments = true) =>
  api.post('/data/start-new-year', null, { params: { archive_students: archiveStudents, clear_attendance: clearAttendance, clear_assessments: clearAssessments } });

// Advisory Management
export const getAdvisoryConfig = () => api.get('/advisory/config');
export const updateAdvisoryConfig = (data) => api.put('/advisory/config', data);
export const assignStaffToAdvisory = (groupNum, staffId) => api.put(`/advisory/assign-staff/${groupNum}`, { staff_id: staffId });
export const assignStudentToAdvisory = (groupNum, studentId) => api.put(`/advisory/assign-student/${groupNum}`, { student_id: studentId });
export const addAdvisoryGroup = () => api.post('/advisory/add-group');
export const removeAdvisoryGroup = (groupNum) => api.delete(`/advisory/remove-group/${groupNum}`);


// Class Groups (Morning/Afternoon)
export const getClassGroups = () => api.get('/class-groups');
export const updateClassGroupsConfig = (data) => api.put('/class-groups/config', data);
export const addClassGroup = (period) => api.post('/class-groups/add', { period });
export const removeClassGroup = (period, groupKey) => api.delete(`/class-groups/${period}/${groupKey}`);
export const assignStudentToClassGroup = (studentId, period, groupKey) => api.put('/class-groups/assign-student', { student_id: studentId, period, group_key: groupKey });

// Group Images
export const uploadGroupImage = async (period, groupKey, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const response = await fetch(`${baseUrl}/api/class-groups/${period}/${groupKey}/image`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw { response: { data: error } };
  }
  return { data: await response.json() };
};
export const deleteGroupImage = (period, groupKey) => api.delete(`/class-groups/${period}/${groupKey}/image`);

export const uploadAdvisoryImage = async (groupNum, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const response = await fetch(`${baseUrl}/api/advisory/${groupNum}/image`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw { response: { data: error } };
  }
  return { data: await response.json() };
};
export const deleteAdvisoryImage = (groupNum) => api.delete(`/advisory/${groupNum}/image`);

// Import Preview
export const previewImportStudents = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  
  const response = await fetch(`${baseUrl}/api/students/import/preview`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      // Don't set Content-Type - browser will set it with boundary for FormData
    },
    body: formData,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw { response: { data: error } };
  }
  
  return { data: await response.json() };
};

// Notifications
export const getNotifications = (unreadOnly = false, limit = 50) => 
  api.get('/notifications', { params: { unread_only: unreadOnly, limit } });
export const markNotificationRead = (notificationId) => 
  api.put(`/notifications/${notificationId}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/mark-all-read');
export const deleteNotification = (notificationId) => 
  api.delete(`/notifications/${notificationId}`);
export const checkMissingAttendance = () => api.post('/attendance/check-missing');

// Block Schedules
export const getBlockSchedules = () => api.get('/block-schedules');
export const updateBlockSchedule = (type, data) => api.put(`/block-schedules/${type}`, data);
export const createCustomSchedule = (data) => api.post('/block-schedules/custom', data);
export const deleteCustomSchedule = (date) => api.delete(`/block-schedules/custom/${date}`);

// Master Schedule
export const createMasterSchedule = (data) => api.post('/master-schedule', data);
export const listMasterSchedules = () => api.get('/master-schedule');
export const getMasterSchedule = (id) => api.get(`/master-schedule/${id}`);
export const updateMasterSchedule = (id, data) => api.put(`/master-schedule/${id}`, data);
export const deleteMasterSchedule = (id) => api.delete(`/master-schedule/${id}`);
export const getMasterScheduleHighlightGroups = (date) => api.get('/master-schedule/highlight-groups', { params: { date } });
export const getMasterScheduleDayClasses = (date) => api.get('/master-schedule/day-classes', { params: { date } });
export const updateScheduleEntry = (id, data) => api.put(`/master-schedule/${id}/entry`, data);
export const updateBulkEntries = (id, data) => api.put(`/master-schedule/${id}/bulk-entries`, data);
export const prefillSchedule = (id, sourceId) => api.post(`/master-schedule/${id}/prefill`, { source_id: sourceId });
export const getScheduleConflicts = (id) => api.get(`/master-schedule/${id}/conflicts`);
export const getScheduleHistory = (id) => api.get(`/master-schedule/${id}/history`);
export const undoScheduleChange = (id) => api.post(`/master-schedule/${id}/undo`);
export const restoreScheduleVersion = (id, historyId) => api.post(`/master-schedule/${id}/restore/${historyId}`);
export const getStaffScheduleView = (scheduleId, staffId) => api.get(`/master-schedule/${scheduleId}/staff-view/${staffId}`);
export const getStudentScheduleView = (scheduleId, studentId) => api.get(`/master-schedule/${scheduleId}/student-view/${studentId}`);
export const getStudentClassSchedule = (studentId) => api.get(`/master-schedule/student-schedule/${studentId}`);
export const getScheduleSubjects = () => api.get('/master-schedule/subjects');
export const getCoreClasses = () => api.get('/core-classes');
export const getCoreClassesManage = () => api.get('/core-classes/manage');
export const updateCoreClasses = (classes) => api.put('/core-classes', { classes });
export const setCoreClassActive = (name, active) => api.put('/core-classes/active', { name, active });

// School Operations
export const getSchoolDayNumber = () => api.get('/school-day-number');
export const getSchoolClosures = () => api.get('/school-closures');
export const addSchoolClosure = (data) => api.post('/school-closures', data);
export const deleteSchoolClosure = (id) => api.delete(`/school-closures/${id}`);
export const getDrills = () => api.get('/drills');
export const addDrill = (data) => api.post('/drills', data);
export const deleteDrill = (id) => api.delete(`/drills/${id}`);

// DNA Tracker
export const getDnaTracker = (date) => api.get('/dashboard/dna-tracker', { params: { date } });

// QA Data Report
export const downloadQaDataReport = (schoolYear) => api.get(`/reports/qa-data`, { params: { school_year: schoolYear }, responseType: 'blob' });

// Seed
export const seedData = () => api.post('/seed');

// Semesters
export const getSemesters = (academicYear) => api.get('/semesters', { params: academicYear ? { academic_year: academicYear } : {} });
export const getAllSchoolYears = () => api.get('/semesters/all-years');
export const updateSemesters = (data) => api.put('/semesters', data);

// Tech Info
export const uploadTechInfo = (formData) => api.post('/students/tech-info/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Student Photos
export const uploadStudentPhoto = async (studentId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const response = await fetch(`${baseUrl}/api/students/${studentId}/photo`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw { response: { data: error } };
  }
  return { data: await response.json() };
};
export const deleteStudentPhoto = (studentId) => api.delete(`/students/${studentId}/photo`);

// Printable Group Lists
export const getPrintableGroups = (groupType = 'all') => api.get('/reports/groups/printable', { params: { group_type: groupType } });
export const getClinicianCaseloads = () => api.get('/reports/clinician-caseloads');
export const getScheduleReviews = () => api.get('/reports/schedule-reviews');

// Default Week Schedule
export const updateDefaultWeek = (academicYear, data) => api.put(`/school-calendar/${academicYear}/default-week`, data);

// Alternate Plans (Internships/Jobs)
export const getAlternatePlans = (params = {}) => api.get('/alternate-plans', { params });
export const getUnscheduledBlocks = (date) => api.get('/alternate-plans/unscheduled-blocks', { params: { date } });
export const createAlternatePlan = (data) => api.post('/alternate-plans', data);
export const updateAlternatePlan = (planId, data) => api.put(`/alternate-plans/${planId}`, data);
export const deleteAlternatePlan = (planId) => api.delete(`/alternate-plans/${planId}`);


// New Year Wizard & Archives
export const executeNewYearWizard = (data) => api.post('/new-year-wizard/execute', data);
export const executeSemesterTransition = (data) => api.post('/semester-transition', data);

// Referral Tracker
export const getReferrals = (archived = false) => api.get('/referrals', { params: { archived } });
export const createReferral = (data) => api.post('/referrals', data);
export const updateReferral = (id, data) => api.put(`/referrals/${id}`, data);
export const acceptReferral = (id, data) => api.post(`/referrals/${id}/accept`, data);
export const backfillSendingDistrict = () => api.post('/referrals/backfill-sending-district');
export const getReferralBackfillCandidates = () => api.get('/referrals/backfill-candidates');
export const backfillAcceptReferrals = (studentIds) => api.post('/referrals/backfill-accept', { student_ids: studentIds });
export const getReferralDashboard = () => api.get('/referrals/dashboard');
export const getReorgReview = () => api.get('/referrals/reorg-review');
export const reclassifyReferral = (data) => api.post('/referrals/reclassify', data);
export const deleteReferralHistorical = (hid) => api.delete(`/referrals/historical/${hid}`);
export const getFormerStudentsDashboard = () => api.get('/referrals/former-dashboard');
export const listReferralHistorical = () => api.get('/referrals/historical');
export const importReferralHistorical = (formData) => api.post('/referrals/historical/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const clearReferralHistorical = () => api.delete('/referrals/historical');
export const getArchivedSchoolYears = () => api.get('/archives/school-years');
export const getArchivedStudents = (year) => api.get(`/archives/${year}/students`);
export const getArchivedRoster = (year) => api.get(`/archives/${year}/roster`);
export const getArchivedAttendance = (year, params = {}) => api.get(`/archives/${year}/attendance`, { params });
export const getArchivedAssessments = (year, params = {}) => api.get(`/archives/${year}/assessments`, { params });
export const getArchivedIncidents = (year, params = {}) => api.get(`/archives/${year}/incidents`, { params });
export const restoreArchivedYear = (year, opts = {}) => api.post(`/archives/${year}/restore`, opts);


// Communications
export const getCommunications = (params = {}) => api.get('/communications', { params });
export const getCommunication = (id) => api.get(`/communications/${id}`);
export const createCommunication = (data) => api.post('/communications', data);
export const updateCommunication = (id, data) => api.put(`/communications/${id}`, data);
export const deleteCommunication = (id) => api.delete(`/communications/${id}`);
export const updateCommTaskStatus = (commId, taskId, status) => api.put(`/communications/${commId}/tasks/${taskId}/status`, { status });
export const completeCommFollowUp = (id) => api.put(`/communications/${id}/follow-up/complete`);
export const getCommunicationTypes = () => api.get('/communications/types/list');
export const createCommunicationType = (label) => api.post('/communications/types', { label });
export const deleteCommunicationType = (id) => api.delete(`/communications/types/${id}`);
export const runCommReminders = () => api.post('/communications/reminders/run');
export const suggestCommContacts = (q) => api.get('/communications/contacts/suggest', { params: { q } });

// Quick Tasks (dedicated data model, independent of communications)
export const getQuickTasks = (params = {}) => api.get('/quick-tasks', { params });
export const createQuickTask = (data) => api.post('/quick-tasks', data);
export const updateQuickTask = (id, data) => api.put(`/quick-tasks/${id}`, data);
export const updateQuickTaskStatus = (id, status) => api.put(`/quick-tasks/${id}/status`, { status });
export const deleteQuickTask = (id) => api.delete(`/quick-tasks/${id}`);
export const getMyReminders = () => api.get('/communications/me/reminders');

// Sign-Ups (Block options + Trips & Events)
export const getSignupSlots = () => api.get('/signups/slots');
export const getSignupDayBlocks = (date) => api.get('/signups/day-blocks', { params: date ? { date } : {} });
export const createSignupSlot = (data) => api.post('/signups/slots', data);
export const updateSignupSlot = (id, data) => api.put(`/signups/slots/${id}`, data);
export const deleteSignupSlot = (id) => api.delete(`/signups/slots/${id}`);
export const getSignupOptions = (params = {}) => api.get('/signups/options', { params });
export const createSignupOption = (data) => api.post('/signups/options', data);
export const updateSignupOption = (id, data) => api.put(`/signups/options/${id}`, data);
export const deleteSignupOption = (id) => api.delete(`/signups/options/${id}`);
export const splitSignupOption = (id) => api.post(`/signups/options/${id}/split`);
export const copySignupWeek = (data) => api.post('/signups/options/copy-week', data);
export const reconcileSignupCell = (data) => api.post('/signups/reconcile-cell', data);
export const getSignupPicks = (params = {}) => api.get('/signups/picks', { params });
export const createSignupPick = (data) => api.post('/signups/picks', data);
export const updateSignupPick = (id, data) => api.put(`/signups/picks/${id}`, data);
export const deleteSignupPick = (id) => api.delete(`/signups/picks/${id}`);
export const getSignupWeekOverview = (monday, advisoryGroup) => api.get('/signups/week-overview', { params: { monday, advisory_group: advisoryGroup } });
export const getSignupTrips = (status = "open") => api.get('/signups/trips', { params: { status } });
export const getTripRoster = (tripId) => api.get(`/signups/trips/${tripId}/roster`);
export const archiveSignupOption = (id) => api.post(`/signups/options/${id}/archive`);
export const unarchiveSignupOption = (id) => api.post(`/signups/options/${id}/unarchive`);
export const getStudentUpcomingSignups = (studentId) => api.get(`/signups/student/${studentId}/upcoming`);
export const getMasterScheduleOverlay = (weekMonday) => api.get('/signups/master-schedule-overlay', { params: { week_monday: weekMonday } });
export const getDayRoster = (date) => api.get('/signups/day-roster', { params: { date } });


// Risk Assessments — staff weekly vibe-check on overall student population
export const getRiskCurrentWeek = () => api.get('/risk-assessments/current-week');
export const getRiskWeek = (week) => api.get(`/risk-assessments/week/${week}`);
export const getRiskTrend = (weeks = 8) => api.get('/risk-assessments/trend', { params: { weeks } });
export const getRiskAssessments = (params = {}) => api.get('/risk-assessments', { params });
export const submitRiskAssessment = (data) => api.post('/risk-assessments', data);
export const deleteRiskAssessment = (id) => api.delete(`/risk-assessments/${id}`);
export const sendMondayRecap = () => api.post('/risk-assessments/send-monday-recap');

// Risk band config (Low/Moderate/Elevated/High cutoffs)
export const getRiskConfig = () => api.get('/risk-config');
export const updateRiskConfig = (bands) => api.put('/risk-config', { bands });
export const getRiskQuestions = () => api.get('/risk-questions');
export const updateRiskQuestions = (categories) => api.put('/risk-questions', { categories });

export const getReminderSettings = () => api.get('/reminder-settings');
export const updateReminderSettings = (data) => api.put('/reminder-settings', data);

// Email / notifications status
export const getEmailStatus = () => api.get('/email-status');
export const getEmailSettings = () => api.get('/email-settings');
export const updateEmailSetting = (key, enabled) => api.put('/email-settings', { key, enabled });

// ---------------- Goals / IEP module ----------------
export const getGoals = (params = {}) => api.get('/goals', { params });
export const getGoalsMeta = () => api.get('/goals/meta');
export const createGoal = (payload) => api.post('/goals', payload);
export const updateGoal = (goalId, payload) => api.put(`/goals/${goalId}`, payload);
export const deleteGoal = (goalId) => api.delete(`/goals/${goalId}`);
export const addGoalObjective = (goalId, payload) => api.post(`/goals/${goalId}/objectives`, payload);
export const updateGoalObjective = (goalId, objId, payload) => api.put(`/goals/${goalId}/objectives/${objId}`, payload);
export const deleteGoalObjective = (goalId, objId) => api.delete(`/goals/${goalId}/objectives/${objId}`);
export const appendGoalLog = (goalId, objId, payload) => api.post(`/goals/${goalId}/objectives/${objId}/log`, payload);
export const updateGoalObjectiveNote = (goalId, objId, noteId, payload) => api.put(`/goals/${goalId}/objectives/${objId}/log/${noteId}`, payload);
export const appendGoalLevelLog = (goalId, payload) => api.post(`/goals/${goalId}/log`, payload);
export const confirmGoalOfficial = (goalId) => api.post(`/goals/${goalId}/confirm-official`);
export const rolloverGoal = (goalId) => api.post(`/goals/${goalId}/rollover`);
export const rolloverActiveGoals = (studentId) => api.post(`/goals/student/${studentId}/rollover-active`);
export const undoRolloverBatch = (batchId) => api.post('/goals/undo-rollover-batch', { batch_id: batchId });
export const rolloverAllActiveGoals = () => api.post('/goals/rollover-all-active');
export const getGoalTemplates = () => api.get('/goals/templates');
export const createGoalTemplate = (payload) => api.post('/goals/templates', payload);
export const deleteGoalTemplate = (id) => api.delete(`/goals/templates/${id}`);
export const setGoalDueDates = (studentId, payload) => api.put(`/goals/student/${studentId}/due-dates`, payload);
export const getGoalReportingPeriods = () => api.get('/goals/reporting-periods');
export const createGoalReportingPeriod = (payload) => api.post('/goals/reporting-periods', payload);
export const updateGoalReportingPeriod = (id, payload) => api.put(`/goals/reporting-periods/${id}`, payload);
export const deleteGoalReportingPeriod = (id) => api.delete(`/goals/reporting-periods/${id}`);
export const goalReportPdfUrl = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return `/goals/report-pdf?${qs}`;
};
export const getGoalsDueSoon = (windowDays = 60) => api.get('/goals/due-soon', { params: { window_days: windowDays } });
export const parseGoalDocument = (studentId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('student_id', studentId);
  return api.post('/goals/import/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
};
export const confirmGoalImport = (payload) => api.post('/goals/import/confirm', payload);
// Platform super-admin: per-school goal-import feature flag
export const platformSetSchoolGoalImport = (schoolId, enabled) => api.patch(`/platform/schools/${schoolId}/goal-import`, { enabled });

// ─────────────────────────── Billing Dashboard ───────────────────────────
export const getBillingSchoolYears = () => api.get('/billing/school-years');
export const getBillingRates = (schoolYear) => api.get('/billing/rates', { params: { school_year: schoolYear } });
export const updateBillingRates = (payload) => api.put('/billing/rates', payload);
export const getBillingDashboard = (schoolYear) => api.get('/billing/dashboard', { params: { school_year: schoolYear } });
export const updateBillingCell = (payload) => api.put('/billing/cell', payload);
export const updateBillingStudentMeta = (payload) => api.put('/billing/student-meta', payload);
export const getBillingProration = (studentId, periodKey, schoolYear) =>
  api.get('/billing/proration-suggestion', { params: { student_id: studentId, period_key: periodKey, school_year: schoolYear } });
export const uploadQbRegister = (file, schoolYear) => {
  const fd = new FormData();
  fd.append('file', file);
  if (schoolYear) fd.append('school_year', schoolYear);
  return api.post('/billing/qb-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getQbUnmatched = (schoolYear) => api.get('/billing/qb-unmatched', { params: { school_year: schoolYear } });
export const saveQbUnmatched = (payload) => api.post('/billing/qb-unmatched/save', payload);
export const dismissQbUnmatched = (id) => api.delete(`/billing/qb-unmatched/${id}`);
export const resolveQbUnmatched = (payload) => api.post('/billing/qb-unmatched/resolve', payload);
export const splitQbUnmatched = (payload) => api.post('/billing/qb-unmatched/split', payload);
export const applyDistrictQbPayment = (payload) => api.post('/billing/qb-unmatched/apply-district', payload);
export const getQbBatches = (schoolYear) => api.get('/billing/qb-batches', { params: { school_year: schoolYear } });
export const undoQbBatch = (payload) => api.post('/billing/qb-batches/undo', payload);
export const reverseQbUpload = (file, schoolYear) => {
  const fd = new FormData();
  fd.append('file', file);
  if (schoolYear) fd.append('school_year', schoolYear);
  return api.post('/billing/qb-reverse-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteBillingRecord = (studentId, periodKey, schoolYear, district) =>
  api.delete('/billing/record', { params: { student_id: studentId, period_key: periodKey, school_year: schoolYear, district } });
export const deleteBillingStudentRow = (studentId, schoolYear, district) =>
  api.delete('/billing/student-row', { params: { student_id: studentId, school_year: schoolYear, district } });
export const getBillingDistricts = (schoolYear) => api.get('/billing/districts', { params: { school_year: schoolYear } });
export const mergeBillingDistricts = (payload) => api.post('/billing/merge-districts', payload);

// ---- Report attachments ----
export const listReportAttachments = (reportType, reportId) =>
  api.get(`/report-attachments/${reportType}/${reportId}`);
export const uploadReportAttachment = (reportType, reportId, file, description = '') => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('description', description || '');
  return api.post(`/report-attachments/${reportType}/${reportId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const updateReportAttachment = (attId, description) =>
  api.put(`/report-attachments/${attId}`, { description });
export const deleteReportAttachment = (attId, reason) =>
  api.delete(`/report-attachments/${attId}`, { data: { reason } });
export const reportAttachmentViewUrl = (attId) =>
  `${API_URL}/report-attachments/view/${attId}?token=${encodeURIComponent(localStorage.getItem('token') || '')}`;
export const reportAttachmentDownloadUrl = (attId) =>
  `${API_URL}/report-attachments/download/${attId}?token=${encodeURIComponent(localStorage.getItem('token') || '')}`;

// ---- Bullying / harassment monitor ----
export const getBullyingConfig = () => api.get('/bullying/config');
export const updateBullyingConfig = (payload) => api.put('/bullying/config', payload);
export const getBullyingMonitor = () => api.get('/bullying/monitor');
export const recomputeBullying = () => api.post('/bullying/recompute');
export const getStudentBullyingFlags = (studentId) => api.get(`/bullying/student/${studentId}/flags`);
export const dismissBullyingWarning = (pairKey, reason) => api.post('/bullying/dismiss', { pair_key: pairKey, reason });
export const getBullyingInvestigations = (status) => api.get('/bullying/investigations', { params: status ? { status } : {} });
export const getBullyingInvestigation = (invId) => api.get(`/bullying/investigations/${invId}`);
export const updateBullyingInvestigation = (invId, payload) => api.put(`/bullying/investigations/${invId}`, payload);
export const generateInvestigationPacket = (invId, payload) =>
  api.post(`/bullying/investigations/${invId}/packet`, payload, { responseType: 'blob' });
export const downloadInvestigationPacket = (invId, packetId) =>
  api.get(`/bullying/investigations/${invId}/packet/${packetId}`, { responseType: 'blob' });

// ---- HHB notification letters ----
export const getHhbSettings = () => api.get('/hhb/settings');
export const updateHhbSettings = (payload) => api.put('/hhb/settings', payload);
export const getHhbTemplates = () => api.get('/hhb/templates');
export const updateHhbTemplate = (key, text) => api.put(`/hhb/templates/${key}`, { text });
export const resetHhbTemplate = (key) => api.post(`/hhb/templates/${key}/reset`);
export const getInvestigationDue = (invId) => api.get(`/hhb/investigations/${invId}/due`);
export const planHhbLetters = (invId, payload) => api.post(`/hhb/investigations/${invId}/letters/plan`, payload);
export const nameCheckHhbLetter = (invId, payload) => api.post(`/hhb/investigations/${invId}/letters/name-check`, payload);
export const finalizeHhbLetters = (invId, payload) => api.post(`/hhb/investigations/${invId}/letters/finalize`, payload);
export const listHhbLetters = (invId) => api.get(`/hhb/investigations/${invId}/letters`);
export const downloadHhbLetter = (invId, letterId, envelope) =>
  api.get(`/hhb/investigations/${invId}/letters/${letterId}/pdf`, { params: { envelope }, responseType: 'blob' });
export const printAllHhbLetters = (invId, stage, envelopes) =>
  api.get(`/hhb/investigations/${invId}/letters/print-all`, { params: { stage, envelopes }, responseType: 'blob' });
export const markHhbLetterSent = (invId, letterId, payload) =>
  api.post(`/hhb/investigations/${invId}/letters/${letterId}/sent`, payload);


export default api;
